import React, { useEffect, useState } from 'react';
import { Coins, Loader2, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';

/**
 * Top-up dialog. Server holds the price tiers — the user chooses a package
 * (or types a custom amount ≥ $5) and we redirect to Stripe Checkout. Amount
 * NEVER passed from client to preserve pricing integrity.
 *
 * Props:
 *   open, onOpenChange     dialog open state
 *   wallet                 optional live wallet summary (for context)
 *   onOpened               optional callback triggered after checkout redirect
 *                          initiated (for wallet refresh on return)
 */
export default function TopUpDialog({ open, onOpenChange, wallet, onOpened }) {
  const account = getCurrentAccount();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState('growth');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !account?.id) return;
    api.get('/credits/packages', { params: { account_id: account.id } })
      .then(({ data }) => {
        setPackages(data.packages || []);
        if (data.packages?.length && !data.packages.find((p) => p.id === selected)) {
          setSelected(data.packages[0].id);
        }
      }).catch(() => setPackages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account?.id]);

  const startCheckout = async () => {
    if (!account?.id || !selected) return;
    setBusy(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post(
        '/credits/checkout',
        { package_id: selected, origin_url: originUrl },
        { params: { account_id: account.id } },
      );
      if (!data?.url) throw new Error('No checkout URL returned');
      onOpened && onOpened();
      // Redirect to Stripe Checkout (leaves the app)
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not start checkout');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="topup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" />
            Top up your AI credits
          </DialogTitle>
          <DialogDescription className="text-xs">
            Credits power the AI Agent (Astrid), portal scraping and bill parsing.
            1 credit = $0.10. Payments are handled by Stripe.
          </DialogDescription>
        </DialogHeader>

        {wallet && (
          <div className="rounded-none border border-slate-200 bg-slate-50 px-3 py-2 text-xs flex items-center justify-between" data-testid="topup-current-balance">
            <span className="text-slate-600">Current balance</span>
            <span className="font-semibold text-slate-800 inline-flex items-center gap-1">
              <Coins className="h-3.5 w-3.5 text-amber-500" />
              {(wallet.balance_credits || 0).toFixed(1)} credits
              <span className="text-slate-500 font-normal ml-1">
                (≈ ${(wallet.balance_usd || 0).toFixed(2)})
              </span>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="topup-packages">
          {packages.map((p) => {
            const active = selected === p.id;
            const totalCredits = p.credits + (p.bonus_credits || 0);
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`topup-pkg-${p.id}`}
                onClick={() => setSelected(p.id)}
                className={`text-left rounded-none border-2 p-3 transition-all ${
                  active
                    ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                    : 'border-slate-200 hover:border-slate-400 bg-white'
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-bold text-slate-900">{p.label}</span>
                  <span className="text-lg font-black text-slate-900">${p.usd.toFixed(0)}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  <span className="font-semibold text-slate-800">{totalCredits}</span> credits
                  {p.bonus_credits > 0 && (
                    <span className="ml-1 text-emerald-700">(+{p.bonus_credits} bonus)</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{p.tagline}</div>
              </button>
            );
          })}
        </div>

        <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
          <Sparkles className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
          <span>
            You'll be redirected to Stripe Checkout to complete payment (test mode).
            Credits are added to your wallet automatically once payment succeeds.
          </span>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between sm:items-center">
          <span className="text-[11px] text-slate-400">
            <ExternalLink className="h-3 w-3 inline mr-1" /> secured by Stripe
          </span>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} data-testid="topup-cancel">
              Cancel
            </Button>
            <Button
              className="rounded-none bg-emerald-600 hover:bg-emerald-700"
              onClick={startCheckout}
              disabled={busy || !selected}
              data-testid="topup-checkout-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Coins className="h-4 w-4 mr-1" />}
              Continue to payment
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
